import { useState, useEffect, useCallback, useMemo } from 'react'
import { PhotosApi, PhotoWithTrip } from '@/shared/api/photos'
import { useAuth } from '@/modules/auth'

export interface TripPhotoGroup {
  tripId: string
  tripName: string
  photos: PhotoWithTrip[]
}

interface UseAllPhotosReturn {
  photos: PhotoWithTrip[]
  tripGroups: TripPhotoGroup[]
  totalCount: number
  tripCount: number
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useAllPhotos(): UseAllPhotosReturn {
  const [photos, setPhotos] = useState<PhotoWithTrip[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { getAccessToken } = useAuth()

  const api = useMemo(() => new PhotosApi(getAccessToken), [getAccessToken])

  const fetchAllPhotos = useCallback(async () => {
    try {
      setError(null)
      const { photos: fetched } = await api.getAllUserPhotos()
      setPhotos(fetched)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch photos')
    }
  }, [api])

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      await fetchAllPhotos()
      setIsLoading(false)
    }
    load()
  }, [fetchAllPhotos])

  const tripGroups = useMemo((): TripPhotoGroup[] => {
    const groupMap = new Map<string, TripPhotoGroup>()
    for (const photo of photos) {
      const key = photo.tripId
      if (!groupMap.has(key)) {
        groupMap.set(key, { tripId: key, tripName: photo.tripName, photos: [] })
      }
      groupMap.get(key)!.photos.push(photo)
    }
    return Array.from(groupMap.values())
  }, [photos])

  return {
    photos,
    tripGroups,
    totalCount: photos.length,
    tripCount: tripGroups.length,
    isLoading,
    error,
    refetch: fetchAllPhotos
  }
}
